import {
  Box,
  InputLabel,
  OutlinedInput,
  OutlinedInputProps,
  Skeleton,
  SxProps,
  TextFieldProps,
} from '@mui/material';
import { HelperTextProps } from '../HelperText/HelperText';
import ErrorIcon from '../../assets/icons/ErrorIcon';
import LockIcon from '../../assets/icons/LockIcon';
import {
  RootBoxSx,
  OutlinedInputSx,
  InputLabelSx,
  lockedTextFieldSx,
} from './TextFieldProps';
import React, { FocusEventHandler } from 'react';
import { ComponentTestIds } from '../../utils/dataTestIds';
import { MouseEventHandler } from 'react';

type Props = Pick<
  TextFieldProps,
  | 'value'
  | 'onChange'
  | 'defaultValue'
  | 'name'
  | 'className'
  | 'placeholder'
  | 'label'
  | 'required'
  | 'disabled'
  | 'multiline'
  | 'fullWidth'
  | 'size'
  | 'InputProps'
  | 'focused'
  | 'type'
  | 'minRows'
  | 'maxRows'
> & {
  error?: boolean;
  warning?: boolean;
  errors?: string[];
  endAdornmentDivider?: boolean;
  helperText?: React.ReactElement<HelperTextProps>;
  startAdornment?: React.ReactElement;
  endAdornment?: React.ReactElement;
  sx?: SxProps;
  tooltip?: React.ReactNode;
  autocompleteHtmlAttribute?: string;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onClick?: MouseEventHandler<HTMLDivElement> | undefined;
  onKeyDown?: React.KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  maxLength?: number;
  readOnly?: boolean;
  locked?: boolean;
  'data-testid'?: string;
  loading?: boolean;
  slotProps?: OutlinedInputProps['slotProps'];
};

export function TextFieldLoader({
  helperText,
  label,
  multiline,
}: {
  helperText: Props['helperText'];
  label: Props['label'];
  multiline?: Props['multiline'];
}) {
  if (multiline) {
    return <Skeleton height={'100%'} variant="rounded" />;
  }
  return (
    <Box>
      {label && (
        <Skeleton
          width={'75%'}
          height={20}
          variant="rounded"
          sx={{ marginBottom: '4px' }}
        />
      )}
      <Skeleton height={44} variant="rounded" />
      {helperText && (
        <Skeleton height={19.91} variant="rounded" sx={{ marginTop: '3px' }} />
      )}
    </Box>
  );
}

const TextField = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    label,
    required,
    disabled,
    multiline,
    className,
    error = false, // Default to false to prevent uncontrolled behavior
    warning = false,
    fullWidth,
    helperText,
    startAdornment,
    endAdornment,
    endAdornmentDivider,
    InputProps,
    sx,
    focused,
    ['data-testid']: dataTestId,
    value,
    tooltip,
    onChange,
    autocompleteHtmlAttribute,
    onBlur,
    onClick,
    onKeyDown,
    maxLength = undefined,
    slotProps: callerSlotProps,
    readOnly,
    locked = false,
    ...rest
  } = props;

  const errorType = error ? 'error' : warning ? 'warning' : undefined;

  function endAdornmentProp() {
    if (
      !endAdornment &&
      !endAdornmentDivider &&
      !error &&
      !warning &&
      !locked
    ) {
      return {};
    }

    return {
      endAdornment: (
        <div className="endAdornmentContainer">
          {error || warning ? (
            tooltip ? (
              tooltip
            ) : (
              <ErrorIcon datatest-id={ComponentTestIds.Textfield.ErrorIcon} />
            )
          ) : null}
          {endAdornmentDivider && (
            <div className="separator" data-testid="separator" />
          )}
          {endAdornment}
          {locked && <LockIcon />}
        </div>
      ),
    };
  }

  return (
    <Box
      className={`TextField ${multiline ? 'multiline' : ''} ${className ?? ''} ${fullWidth ? 'fullWidth' : ''}`}
      sx={
        {
          ...RootBoxSx,
          ...sx,
        } as SxProps
      }
      data-testid={dataTestId ?? 'textfield'}
    >
      {props.loading ? (
        <TextFieldLoader
          helperText={helperText}
          label={label}
          multiline={multiline}
        />
      ) : (
        <>
          {label ? (
            <InputLabel
              htmlFor={props.name}
              required={required}
              disabled={disabled}
              error={error}
              sx={InputLabelSx({ type: errorType })}
              focused={!!focused}
            >
              {label}
            </InputLabel>
          ) : null}

          <OutlinedInput
            {...rest}
            {...InputProps}
            id={props.name}
            value={value} // Controlled value
            onChange={onChange} // Controlled onChange
            fullWidth
            slotProps={{
              input: {
                autoComplete: autocompleteHtmlAttribute ?? '',
                maxLength,
                ...callerSlotProps?.input,
              },
            }}
            disabled={disabled}
            readOnly={locked || readOnly}
            inputRef={ref}
            multiline={multiline}
            error={error}
            {...(startAdornment ? { startAdornment } : null)}
            {...endAdornmentProp()}
            onBlur={onBlur}
            onClick={onClick}
            onKeyDown={onKeyDown}
            className={errorType === 'warning' ? 'warning' : ''}
            sx={
              locked
                ? { ...OutlinedInputSx, ...lockedTextFieldSx }
                : OutlinedInputSx
            }
          />
          {helperText}
        </>
      )}
    </Box>
  );
});

TextField.displayName = 'TextField';

export default TextField;
